import React, { useEffect, useState } from 'react';
import { injectIntl, FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import { connect } from 'dva';
import { Prompt } from 'dva/router';
import _ from 'lodash';
import {
  Form,
  Input,
  Checkbox,
  Select,
  Card,
  Icon,
  Row,
  Button,
  Modal,
  Spin,
  Col,
  Popover,
  Collapse,
  Comment,
  Avatar,
} from 'antd';
import { CancelToken } from 'axios';
import PopoverText from '../PopoverText';
import styles from '../styles.less';
import * as projectService from '../../../services/projects';
import {
  ButtonBar,
  NavModules,
  AllowedDomains,
  PageHeader,
  TwoColumnsSelect,
  SelectInput,
  RemoteSelectInput,
} from '../../index';
import config from '../../../config';
import { searchCompanyByNameRequest } from '../../../services/companies';

const { TextArea } = Input;

const updateHubspotToken = (projectId, props, setHubspotPortalId) => {
  const tokenValue = props.form.getFieldValue('hubspot_token');
  if (tokenValue === null) {
    return;
  }

  return Modal.confirm({
    title: props.intl.formatMessage({ id: 'Common.Attention' }),
    content: props.intl.formatMessage({ id: 'Component.AccountSettings.New.HubspotSection.ConfirmTokenUpdate' }),
    okText: props.intl.formatMessage({ id: 'Common.Save' }),
    cancelText: props.intl.formatMessage({ id: 'Common.Cancel' }),
    okType: 'danger',
    onOk: () => {
      props.dispatch({
        type: 'projects/updateHubspotToken',
        payload: { projectId, hubspot_token: tokenValue },
      }).then((result) => {
        setHubspotPortalId(result.hubspot_portal_id);
        props.form.resetFields(['hubspot_token']);
        props.dispatch({
          type: 'app/addNotification',
          payload: {
            type: 'success',
            message: 'Common.Success',
            description: 'Component.AccountSettings.Edit.Success.Text',
          },
        }).then(() => props.dispatch({ type: 'app/showNotifications' }));
      });
    },
  });
};

const companiesLoader = (projectId) => {
  return (query) => {
    let cancel;

    const requestConfig = {
      cancelToken: new CancelToken((c) => {
        cancel = c;
      }),
    };

    return {
      request: searchCompanyByNameRequest({ projectId, query }, requestConfig),
      cancel,
    };
  };
};

const llmEnabled = config['features.llm_company_visit_context_completion'];

const ProjectItem = (props) => {
  const { id } = props.match.params;
  const projectId = id;

  const [isSubmitted, setSubmitted] = useState(false);
  const [dimensionDisabled, setDimensionDisabled] = useState(true);
  const [buttonDisabled, setButtonState] = useState(false);
  const [users, setUsers] = useState(props.projects.projectDetail.users || []);
  const [hubspotPermissions, setHubspotPermissions] = useState(props.projects.projectDetail.hubspot_permissions || []);
  const [hubspotPortalId, setHubspotPortalId] = useState(props.projects.projectDetail.hubspot_portal_id);
  const [dealStages, setDealStages] = useState([]);
  const [createHubspotDeal, setCreateHubspotDeal] = useState(false);
  const [llmPromptResult, setLlmPromptResult] = useState(null);
  const [llmPromptInProgress, setLlmPromptInProgress] = useState(false);
  const [llmPromptTestCompanyId, setLlmPromptTestCompanyId] = useState(null);
  const pipelines = props.hubspot.pipelines[projectId] || [];

  const modulesList = NavModules();
  const modulesKeys = Object.keys(modulesList);
  const editMode = !!id;

  useEffect(() => {
    props.dispatch({
      type: 'header/update',
      payload: {
        title: 'Component.AccountSettings.List.Title', description: 'Component.AccountSettings.List.Description1',
      },
    });

    props.dispatch({
      type: 'users/loadList',
    });

    if (id) {
      props.dispatch({
        type: 'projects/loadDetail', includes: ['users', 'modules', 'hubspotPermissions'], payload: { id },
      }).then((data) => {
        const {
          matomo_site_id, lxp_id_visit_dimension_id, lxp_id_action_dimension_id, target_group_id_visit_dimension_id,
        } = data;
        setCreateHubspotDeal(data.hubspot_create_deals);
        const isDisabled = () => {
          return !(matomo_site_id || lxp_id_visit_dimension_id || lxp_id_action_dimension_id || target_group_id_visit_dimension_id);
        };
        setDimensionDisabled(isDisabled());
      });
    }
  }, []);

  useEffect(() => {
    setUsers(props.projects.projectDetail.users);
    setHubspotPermissions(props.projects.projectDetail.hubspot_permissions);
  }, [props.projects.projectDetail]);

  useEffect(() => {
    if (props.app.currentProject) {
      handleHubspotPipelineChange(props.app.currentProject.hubspot_pipeline_default_id);
    }
  }, [props.hubspot, props.app.currentProject]);

  useEffect(() => {
    if (!id || !props.projects.projectDetail.hubspot_portal_id) {
      return;
    }

    setHubspotPortalId(props.projects.projectDetail.hubspot_portal_id);

    props.dispatch({
      type: 'hubspot/loadPipelines',
      payload: {
        projectId,
      },
    });

    props.dispatch({
      type: 'hubspotOwners/all',
      payload: {
        projectId,
      },
    });

    props.dispatch({
      type: 'hubspotOwners/assigned',
      payload: {
        projectId,
      },
    });
  }, [props.projects.projectDetail]);


  const backToOverview = () => {
    props.dispatch({
      type: 'app/goToRoute', payload: { routeName: 'ACCOUNTS_LIST' },
    });
  };

  const generateToken = () => {
    Modal.confirm({
      title: props.intl.formatMessage({ id: 'Common.Attention' }),
      content: props.intl.formatMessage({ id: 'Component.AccountSettings.RegenerateTokenModal.Text' }),
      onOk: () => {
        props.dispatch({ type: 'projects/generateToken', payload: { projectId: id } }).then((result) => {
          const modal = Modal.info({
            title: props.intl.formatMessage({ id: 'Component.AccountSettings.TokenModal.Text' }),
            content: <Input.TextArea value={result.auth_token} readOnly />,
            onOk: () => {
              modal.destroy();
            },
          });
        });
      },
      cancelText: props.intl.formatMessage({ id: 'Common.Cancel' }),
    });
  };

  const addUser = () => {
    setButtonState(true);
    setUsers([...(users || []), { id: null, role_id: 1 }]);
  };

  const setUserName = (index, userId) => {
    setButtonState(false);
    setUsers(users.map((item, i) => (index === i ? {
      ...props.users.usersList.find(user => user.id === userId), role_id: 1,
    } : item)));
  };

  const removeUser = (index) => {
    setUsers(users.filter((user, i) => index !== i));
    setButtonState(!users.length);
  };

  const getRemainingUsers = (index) => {
    return props.users.usersList.filter((user) => {
      return !_.without(users, users[index]).some(item => item.id === user.id);
    });
  };

  const handleDimensionFields = (event) => {
    if (event.target.value && /^\d+$/.test(event.target.value)) {
      setDimensionDisabled(false);
    } else {
      setDimensionDisabled(true);
      props.form.resetFields(['target_group_id_visit_dimension_id', 'lxp_id_visit_dimension_id', 'lxp_id_action_dimension_id']);
    }
  };

  const handleHubspotPipelineChange = (v) => {
    const pipeline = pipelines.find(p => p.id === v);
    setDealStages(pipeline ? pipeline.stages : []);
  };

  const getInitialDimensionValue = (fieldName) => {
    return !dimensionDisabled ? props.projects.projectDetail[fieldName] : null;
  };

  const createProject = (payload) => {
    props.dispatch({
      type: 'projects/create', payload,
    }).then((data) => {
      if (data && data.status) {
        const modal = Modal.info({
          title: props.intl.formatMessage({ id: 'Component.AccountSettings.TokenModal.Text' }),
          content: <Input.TextArea value={data.authToken} readOnly />,
          onOk: () => {
            modal.destroy();
            props.dispatch({ type: 'projects/redirectToProjectsList' });
          },
        });
      }
    });
  };

  const testLlmPrompt = async (companyId, prompt) => {
    setLlmPromptInProgress(true);
    const result = await projectService.testLlmPrompt(projectId, companyId, prompt);
    setLlmPromptInProgress(false);
    setLlmPromptResult(result.text);
  };

  const updateProject = async (data) => {
    const success = await props.dispatch({
      type: 'projects/update', payload: data,
    });

    if (!success) {
      // project stored within few requests, so stop in case of any error
      // partial state will be stored
      return;
    }

    if (data.hubspot_users_selected) {
      const owners = await props.dispatch({
        type: 'hubspotOwners/replaceOwners', payload: { projectId, ownersIds: data.hubspot_users_selected },
      });

      if (!owners) {
        // stop update
        return;
      }
    }

    // store hubspot user permissions
    if (data.lxp_hubspot_users_selected) {
      const permissions = await props.dispatch({
        type: 'hubspot/replaceHubspotUserPermissions', payload: { projectId, userIds: data.lxp_hubspot_users_selected },
      });

      if (!permissions) {
        // stop update
        return;
      }
    }

    if (data.hubspot_features && data.hubspot_features.length > 0) {
      const features = await props.dispatch({
        type: 'hubspot/updateSettings',
        payload: {
          projectId,
          hubspot_create_tasks: data.hubspot_features.includes('hubspot_create_tasks'),
          // deal
          hubspot_create_deals: data.hubspot_features.includes('hubspot_create_deals'),
          hubspot_pipeline_default_id: data.hubspot_pipeline_default_id,
          hubspot_pipeline_default_stage_id: data.hubspot_pipeline_default_stage_id,
          // comments
          hubspot_create_comment_on_new_visit: data.hubspot_features.includes('hubspot_create_comment_on_new_visit'),
        },
      });

      if (!features) {
        // stop update
        return;
      }
    }

    props.dispatch({ type: 'app/showNotifications' });
    props.dispatch({ type: 'app/goToRoute', payload: { routeName: 'ACCOUNTS_LIST' } });
  };

  const toNumber = value => (value !== '' ? parseInt(value, 10) : null);

  const formatData = data => (data ? data.filter(Boolean).join(',') : '');

  const handleSubmit = (e) => {
    e.preventDefault();
    props.form.validateFieldsAndScroll(async (err, values) => {
      /*  eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["values"] }]*/
      values.allowed_domains = formatData(values.allowed_domains) || null;
      values.matomo_site_id = toNumber(values.matomo_site_id);
      values.target_group_id_visit_dimension_id = toNumber(values.target_group_id_visit_dimension_id);
      values.lxp_id_visit_dimension_id = toNumber(values.lxp_id_visit_dimension_id);
      values.lxp_id_action_dimension_id = toNumber(values.lxp_id_action_dimension_id);

      if (!err) {
        setSubmitted(true);
        const matomoId = parseInt(values.matomo_site_id, 10) || null;
        const selectedUsers = values.users ? values.users.filter((item) => {
          return item.id && item.role_id;
        }) : [];

        const payload = {
          ...values, id, users: selectedUsers, matomo_site_id: matomoId,
        };

        if (id) {
          await updateProject(payload);
        } else {
          await createProject(payload);
        }
      }
    });
  };

  const cancelAction = () => {
    props.dispatch({
      type: 'app/goToRoute', payload: { routeName: 'ACCOUNTS_LIST' },
    });
  };

  return (<Spin
    spinning={!!props.loading.effects['projects/update'] ||
      !!props.loading.effects['projects/create'] ||
      !!props.loading.effects['projects/loadDetail'] ||
      !!props.loading.effects['projects/generateToken'] ||
      !!props.loading.effects['projects/updateHubspotToken'] ||
      !!props.loading.effects['hubspotOwners/loadOwners'] ||
      !!props.loading.effects['hubspotOwners/loadAssignedOwners'] ||
      !!props.loading.effects['hubspotOwners/replaceOwners'] ||
      !!props.loading.effects['hubspot/replaceHubspotUserPermissions'] ||
      !!props.loading.effects['hubspot/updateSettings']
    }
  >

    <Prompt
      when={(props.form.isFieldsTouched() && !isSubmitted)}
      message={props.intl.locale}
    />
    <div className={[styles.containerLarge, styles.containerProject].join(' ')}>
      <div className={[styles.componentHeader]}>
        <PageHeader onBackToOverview={backToOverview}>
          <FormattedMessage id={`Component.AccountSettings.${id ? 'Edit.Title' : 'New.Title1'}`} />
        </PageHeader>
      </div>
      <Form onSubmit={handleSubmit}>
        <Collapse
          bordered={false}
          defaultActiveKey={['0']}
          expandIconPosition="right"
        >
          <Collapse.Panel
            key="account"
            header={<h3><FormattedMessage id="Component.AccountSettings.New.AccountSection.Account" /></h3>}
          >
            <Card className={[styles.noMarginTop, styles.noMarginBottom, styles.noBorderTop].join(' ')}>
              <Row type={'flex'} gutter={32}>
                <Col span={12}>
                  <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.Project.Name" />}>
                    {props.form.getFieldDecorator('name', {
                      initialValue: props.projects.projectDetail.name || '',
                      rules: [{
                        required: true, message: props.intl.formatMessage({ id: 'Common.Field.Required' }),
                      }],
                    })(<Input />)}
                  </Form.Item>
                  <Form.Item label={<FormattedMessage id="Component.AccountSettings.New.ModulesSection.Title" />}>
                    {props.form.getFieldDecorator('modules', {
                      initialValue: props.projects.projectDetail.modules || [],
                      rules: [{
                        required: true, message: props.intl.formatMessage({ id: 'Common.Field.Required' }),
                      }],
                    })(<Checkbox.Group>
                      {modulesKeys.map(item => (<Checkbox
                        className={styles.Checkbox}
                        value={modulesList[item].code}
                        key={item}
                      >
                        {modulesList[item].label}
                      </Checkbox>))}
                    </Checkbox.Group>)}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <AllowedDomains form={props.form} isNew={!id} />
                </Col>
              </Row>
              {id && (<Row>
                <Col>
                  <Button className={styles.topMiddleMargin} type="danger" onClick={generateToken}>
                    <FormattedMessage id="Component.AccountSettings.RegenerateToken" />
                  </Button>
                </Col>
              </Row>)}
            </Card>
          </Collapse.Panel>
          <Collapse.Panel
            key="users"
            header={<h3><FormattedMessage id="Component.AccountSettings.New.UserSection.User" /></h3>}
          >
            <Card className={[styles.noMarginTop, styles.noMarginBottom, styles.noBorderTop].join(' ')}>
              <Row type="flex" gutter={32}>
                {(users || []).map((user, index) => {
                  return (<Col key={index} md={24} lg={12}>
                    <div className={styles.userRoleCard}>
                      <Row type="flex" gutter={32}>
                        <Col span={17} className={styles.userRoleColumn}>
                          <Form.Item>
                            <h4><FormattedHTMLMessage
                              id="Component.AccountSettings.New.UserSection.User"
                            /> {index + 1}</h4>
                            {props.form.getFieldDecorator(`users[${index}].id`, {
                              rules: [{
                                type: 'number', message: props.intl.formatMessage({ id: 'Common.Field.Required' }),
                              }],
                              initialValue: user.id || '',
                            })(<Select onSelect={setUserName.bind(this, index)}>
                              {(getRemainingUsers(index) || []).map(item => (<Select.Option
                                className={styles.Checkbox}
                                value={item.id}
                                key={`${item.id} ${index}`}
                              >
                                {item.first_name} {item.last_name}
                              </Select.Option>))}
                            </Select>)}
                          </Form.Item>
                          <Form.Item>
                            {props.form.getFieldDecorator(`users[${index}].role_id`, {
                              rules: [{
                                type: 'number', message: props.intl.formatMessage({ id: 'Common.Field.Required' }),
                              }],
                              initialValue: user.role_id || '',
                            })(<Select>
                              {(props.app.roles || []).map(item => (
                                <Select.Option className={styles.Checkbox} value={item.id} key={item.id}>
                                  {item.label}
                                </Select.Option>))}
                            </Select>)}
                          </Form.Item>
                        </Col>
                        <Col className={styles.userRoleColumnDelete}>
                          <div className={styles.btnNoBorder}>
                            <Icon type="close" onClick={() => removeUser(index)} />
                          </div>
                          <Popover
                            content={<PopoverText />}
                            trigger="click"
                            title={props.intl.formatMessage({ id: 'Component.AccountSettings.New.AvailableRoles.Title' })}
                          >
                            <a href="##" className={styles.btnNoBorder}>
                              <Icon type="question-circle-o" />
                            </a>
                          </Popover>
                        </Col>
                      </Row>
                    </div>
                  </Col>);
                })}
              </Row>
              <Row type="flex" gutter={32}>
                <Col span={7}>
                  {(users ? users.length : 0) < props.users.usersList.length && (<Button
                    size="large"
                    type="primary"
                    disabled={buttonDisabled}
                    onClick={() => addUser()}
                    className={styles.btnWithBlueBorders}
                  >
                    <Icon type="plus" />
                    <FormattedMessage id="Component.AccountSettings.New.UserSection.AddUser" />
                  </Button>)}
                </Col>
              </Row>
            </Card>
          </Collapse.Panel>
          <Collapse.Panel
            key="matomo"
            header={<h3><FormattedMessage id="Component.AccountSettings.New.MatomoSection.Matomo" />
            </h3>}
          >
            <Card className={[styles.noMarginTop, styles.noMarginBottom, styles.noBorderTop].join(' ')}>
              <Row type={'flex'} gutter={32}>
                <Col md={12} lg={6}>
                  <Form.Item
                    label={<FormattedMessage id="Component.AccountSettings.New.DomainSection.MatomoIdName" />}
                  >
                    {props.form.getFieldDecorator('matomo_site_id', {
                      initialValue: (props.projects.projectDetail.matomo_site_id || '').toString(),
                      rules: [{
                        required: false,
                        message: props.intl.formatMessage({ id: 'Common.Field.RequiredAndNumber' }),
                      }],
                      onChange: handleDimensionFields,
                    })(<Input />)}
                  </Form.Item>
                </Col>
                <Col md={12} lg={6}>
                  <Form.Item label="Visit Dimension">
                    {props.form.getFieldDecorator('lxp_id_visit_dimension_id', {
                      initialValue: getInitialDimensionValue('lxp_id_visit_dimension_id'),
                      rules: [{
                        required: !dimensionDisabled,
                        message: props.intl.formatMessage({ id: 'Common.Field.RequiredAndNumber' }),
                      }],
                    })(<Input disabled={dimensionDisabled} />)}
                  </Form.Item>
                </Col>
                <Col md={12} lg={6}>
                  <Form.Item label="Action Dimension">
                    {props.form.getFieldDecorator('lxp_id_action_dimension_id', {
                      initialValue: getInitialDimensionValue('lxp_id_action_dimension_id'),
                      rules: [{
                        required: !dimensionDisabled,
                        message: props.intl.formatMessage({ id: 'Common.Field.RequiredAndNumber' }),
                      }],
                    })(<Input disabled={dimensionDisabled} />)}
                  </Form.Item>
                </Col>
                <Col md={12} lg={6}>
                  <Form.Item label="Target Group Dimension">
                    {props.form.getFieldDecorator('target_group_id_visit_dimension_id', {
                      initialValue: getInitialDimensionValue('target_group_id_visit_dimension_id'),
                      rules: [{
                        required: !dimensionDisabled,
                        message: props.intl.formatMessage({ id: 'Common.Field.RequiredAndNumber' }),
                      }],
                    })(<Input disabled={dimensionDisabled} />)}
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Collapse.Panel>

          {editMode && <Collapse.Panel
            key="hubspot" header={<h3><FormattedMessage
              id="Component.AccountSettings.New.HubspotSection.HubspotTitle"
            /></h3>}
          >
            <Card className={[styles.noMarginTop, styles.noMarginBottom, styles.noBorderTop].join(' ')}>
              <Row type={'flex'} gutter={32}>
                <Col md={12} lg={10}>
                  <Form.Item label="Hubspot Private App API key">
                    {props.form.getFieldDecorator('hubspot_token', {
                      initialValue: null,
                      rules: [{
                        required: false,
                      }],
                    })(<Input
                      placeholder={hubspotPortalId ?
                        props.intl.formatMessage(
                          { id: 'Component.AccountSettings.New.HubspotSection.AccountIntegratedPlaceholder' },
                          { portalId: hubspotPortalId },
                        ) :
                        props.intl.formatMessage({ id: 'Component.AccountSettings.New.HubspotSection.AccountNotIntegratedPlaceholder' })}
                    />)}
                  </Form.Item>
                </Col>
                <Col md={12} lg={2}>
                  <Button
                    disabled={!props.form.getFieldValue('hubspot_token')}
                    type="primary"
                    style={{ marginTop: '48px' }}
                    onClick={() => updateHubspotToken(projectId, props, setHubspotPortalId)}
                  >
                    <FormattedMessage id="Common.Save" />
                  </Button>
                </Col>
              </Row>
              <Row type={'flex'} gutter={32}>
                <Col md={12} lg={10}>
                  <Form.Item
                    label={<FormattedMessage id="Component.AccountSettings.New.HubspotSection.FeaturesLabel" />}
                  >
                    {props.form.getFieldDecorator('hubspot_features', {
                      initialValue: [
                        'hubspot_create_deals',
                        'hubspot_create_tasks',
                        'hubspot_create_comment_on_new_visit',
                      ].filter(v => props.projects.projectDetail[v] === true),
                      rules: [{
                        required: false,
                      }],
                    })(<Checkbox.Group
                      disabled={!hubspotPortalId}
                      options={[
                        {
                          label: <FormattedMessage
                            id="Component.AccountSettings.New.HubspotSection.CreateTaskFeatureLabel"
                          />,
                          value: 'hubspot_create_tasks',
                        },
                        {
                          label: <FormattedMessage
                            id="Component.AccountSettings.New.HubspotSection.CreateDealFeatureLabel"
                          />,
                          value: 'hubspot_create_deals',
                          onChange: (e) => {
                            setCreateHubspotDeal(e.target.checked);
                            if (e.target.checked === false) {
                              props.form.setFieldsValue({
                                hubspot_pipeline_default_id: null,
                                hubspot_pipeline_default_stage_id: null,
                              });
                            }
                          },
                        },
                        {
                          label: <FormattedMessage
                            id="Component.AccountSettings.New.HubspotSection.CreateCommentOnNewVisit"
                          />,
                          value: 'hubspot_create_comment_on_new_visit',
                        },
                      ]}
                    />)}
                  </Form.Item>
                  <Form.Item
                    label={<FormattedMessage id="Component.AccountSettings.New.HubspotSection.DefaultPipelineLabel" />}
                  >
                    {props.form.getFieldDecorator('hubspot_pipeline_default_id', {
                      initialValue: props.projects.projectDetail.hubspot_pipeline_default_id,
                    })(
                      <SelectInput
                        disabled={!createHubspotDeal}
                        showArrow
                        placeholder={<FormattedMessage
                          id="Component.AccountSettings.New.HubspotSection.DefaultPipelinePlaceholder"
                        />}
                        options={pipelines}
                        onChange={(v) => {
                          handleHubspotPipelineChange(v);
                          props.form.setFieldsValue({
                            hubspot_pipeline_default_stage_id: null,
                          });
                        }}
                      />,
                    )}
                  </Form.Item>
                  <Form.Item>
                    {props.form.getFieldDecorator('hubspot_pipeline_default_stage_id', {
                      initialValue: props.projects.projectDetail.hubspot_pipeline_default_stage_id || null,
                    })(
                      <SelectInput
                        disabled={!createHubspotDeal}
                        showArrow
                        placeholder={<FormattedMessage
                          id="Component.AccountSettings.New.HubspotSection.DefaultDealStagePlaceholder"
                        />}
                        options={dealStages}
                      />,
                    )}
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <h3><FormattedMessage id="Component.AccountSettings.New.HubspotSection.SelectUsersTitle" /></h3>
                <p><FormattedMessage id="Component.AccountSettings.New.HubspotSection.SelectUsersDescription" /></p>
                <TwoColumnsSelect
                  disabled={!hubspotPortalId}
                  optionsFieldName="hubspot_users"
                  selectedFieldName="hubspot_users_selected"
                  form={props.form}
                  optionValues={props.hubspotOwners.all.map((owner) => {
                    return { key: owner.hubspot_user_id, title: `${owner.first_name} ${owner.last_name}` };
                  })}
                  selectedValues={Array.isArray(props.hubspotOwners.assigned[projectId]) ? props.hubspotOwners.assigned[projectId].map(owner => owner.hubspot_user_id) : []}
                />
              </Row>
              <Row style={{ marginTop: '32px' }}>
                <h3><FormattedMessage id="Component.AccountSettings.New.HubspotSection.SelectLxpUsersTitle" /></h3>
                <p><FormattedMessage id="Component.AccountSettings.New.HubspotSection.SelectLxpUsersDescription" /></p>
                <TwoColumnsSelect
                  disabled={!hubspotPortalId}
                  optionsFieldName="lxp_hubspot_users"
                  selectedFieldName="lxp_hubspot_users_selected"
                  form={props.form}
                  optionValues={users.map((user) => {
                    return { key: user.id, title: `${user.first_name} ${user.last_name}` };
                  })}
                  selectedValues={hubspotPermissions.map(item => item.user_id)}
                />
              </Row>
            </Card>
          </Collapse.Panel>}
          {/* AI Assistent*/}
          {llmEnabled && editMode && <Collapse.Panel
            key="llm"
            header={<h3><FormattedMessage id="Component.AccountSettings.Edit.LanguageModelSection.LLMPromptTitle" />
            </h3>}
          >
            <Spin spinning={llmPromptInProgress}>
              <Card className={[styles.noMarginTop, styles.noMarginBottom, styles.noBorderTop].join(' ')}>
                <FormattedMessage id="Component.AccountSettings.Edit.LanguageModelSection.LLMPromptDescription1" /><br />
                <FormattedMessage id="Component.AccountSettings.Edit.LanguageModelSection.LLMPromptDescription2" /><br />
                <FormattedMessage id="Component.AccountSettings.Edit.LanguageModelSection.LLMPromptDescription3" />
                <Row type={'flex'} gutter={32}>
                  <Col span={24}>
                    <Form.Item
                      label={<FormattedMessage
                        id="Component.AccountSettings.Edit.LanguageModelSection.CustomizeYourPromptLabel"
                      />}
                    >
                      {props.form.getFieldDecorator('llm_prompt', {
                        initialValue: props.projects.projectDetail.llm_prompt,
                        rules: [{
                          required: false,
                        }],
                      })(<
                        TextArea
                        style={{ height: '200px' }}
                        autoSize={{ minRows: 6, maxRows: 10 }}
                      />)}
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label={<FormattedMessage id="Component.AccountSettings.Edit.LanguageModelSection.CompanyLabel" />}
                    >
                      {props.form.getFieldDecorator('llm_test_company_id', {})(
                        <RemoteSelectInput
                          nameKey="company_name"
                          remoteLoader={companiesLoader(projectId)}
                          notFoundContent={props.intl.formatMessage({ id: 'Component.AccountSettings.Edit.LanguageModelSection.TestCompanyNoCompaniesFound' })}
                          placeholder={props.intl.formatMessage({ id: 'Common.RemoteSelect.Placeholder' })}
                          showArrow
                          onChange={(value) => {
                            setLlmPromptTestCompanyId(value);
                          }}
                        />,
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Button
                      disabled={!llmPromptTestCompanyId}
                      type="primary"
                      style={{ marginTop: '48px' }}
                      onClick={() => testLlmPrompt(
                        props.form.getFieldValue('llm_test_company_id'),
                        props.form.getFieldValue('llm_prompt'),
                      )}
                    >
                      <FormattedMessage id="Common.Test" />
                    </Button>
                  </Col>
                </Row>
                {llmPromptResult &&
                  <Row>
                    <Comment
                      author="AI Sales Assistant"
                      avatar={<Avatar src="/dist/ai-sales-avatar.jpg" />}
                      content={
                        <p style={{ whiteSpace: 'pre-line' }}>
                          {llmPromptResult}
                        </p>
                      }
                    />
                  </Row>}
              </Card>
            </Spin>
          </Collapse.Panel>}
        </Collapse>
        <ButtonBar cancelAction={cancelAction} />
      </Form>
    </div>
  </Spin>);
};

ProjectItem.defaultProps = {
  hubspotOwners: {
    all: [],
    assigned: [],
  },
  hubspot: {
    pipelines: [],
  },
};

export default connect(({
  app, loading, projects, users, hubspotOwners, hubspot,
}) => ({
  app, loading, projects, users, hubspotOwners, hubspot,
}))(Form.create()(injectIntl(ProjectItem)));



// WEBPACK FOOTER //
// ./src/components/Projects/ProjectItem/ProjectItem.js